import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Card,
  Form,
  Col,
  Button,
  Select,
  DatePicker,
  Alert,
  Typography,
  Spin,
  Flex,
  List,
  Table,
  Space,
  Popconfirm,
  Drawer,
  Input,
  message,
  Switch,
  InputNumber,
} from "antd";
import {
  EditOutlined,
  SendOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import "./style.css";

import {
  useFetchTemperatureDataQuery,
  useAddTemperatureDataMutation,
  useDeleteOneTemperatureDataMutation,
} from "./temperature.service";

import { useParams, useNavigate } from "react-router-dom";
import { setTemperatures } from "./temperature.slice";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;
const { Option } = Select;
const { Column } = Table;

const Restaruant = () => {
  const { t } = useTranslation();

  const tempType = useSelector((store) => store.temperature.temperatureType);

  const { data, isLoading, isSuccess } = useFetchTemperatureDataQuery();

  const [addTemperatureData] = useAddTemperatureDataMutation();
  const [deleteTemperatureData] = useDeleteOneTemperatureDataMutation();

  const dispatch = useDispatch();
  const temperatures = useSelector((apps) => apps.temperature.temperatures);

  const [isDrawer, setIsDrawer] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleView = (id) => {
    navigate("/temperatures/view/" + id);
  };

  const handleUpdate = (id) => {
    navigate("/temperatures/edit/" + id);
  };
  const handleDelete = (id) => {
    deleteTemperatureData(id);
  };

  const userData = useSelector((apps) => apps.app.user);

  useEffect(() => {
    if (isSuccess) {
      dispatch(setTemperatures(data?.data));
    }
  }, [isSuccess, data, dispatch]);

  const handleFormSubmit = () => {
    const formData = form.getFieldsValue();

    setIsDrawer(false);
    addTemperatureData({
      ...formData,
      restaurant_id: userData._id,
    });
    form.resetFields();
  };

  if (isLoading)
    return (
      <Flex justify="center" align="center">
        <Spin />
      </Flex>
    );
  return (
    <Card
      title={t("Temperature List")}
      style={{ fontSize: "20px" }}
      extra={
        <Button
          variant="solid"
          type="primary"
          onClick={() => setIsDrawer(true)}
        >
          {t("Create")}
        </Button>
      }
    >
      <Table dataSource={temperatures} rowKey="key">
        <Column
          title={"#"}
          dataIndex={"key"}
          align="center"
          render={(text, record, index) => index + 1}
        />
        <Column
          align="center"
          title={t("Sensor Name")}
          dataIndex={"sensor_id"}
        />
        <Column
          align="center"
          title={t("Min Temperature")}
          dataIndex={"min_temp"}
          render={(min, record, key) =>
            tempType === "celsius"
              ? `${min.toFixed(2)}°C`
              : `${((min * 9) / 5 + 32).toFixed(2)}°F`
          }
        />
        <Column
          align="center"
          title={t("Max Temperature")}
          dataIndex={"max_temp"}
          render={(max, record, key) =>
            tempType === "celsius"
              ? `${max.toFixed(2)}°C`
              : `${((max * 9) / 5 + 32).toFixed(2)}°F`
          }
        />
        <Column
          align="center"
          title={t("Action")}
          render={(text, record, index) => (
            <Space size="middle">
              <Button
                type="default"
                variant="solid"
                color="cyan"
                icon={<EyeOutlined />}
                onClick={() => handleView(record._id)}
                size="small"
              >
                {t("View")}
              </Button>

              <Button
                color="green"
                variant="solid"
                icon={<EditOutlined />}
                onClick={() => handleUpdate(record._id)}
                size="small"
              >
                {t("Edit")}
              </Button>

              <Button
                type="default"
                color="danger"
                variant="solid"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record._id)}
                size="small"
              >
                {t("Delete")}
              </Button>
            </Space>
          )}
        />
      </Table>

      <Drawer
        title={t("Edit temperature")}
        open={isDrawer}
        onClose={() => {
          form.resetFields();
          setIsDrawer(false);
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item
            label={t("Sensor Name")}
            name="sensor_id"
            rules={[
              {
                required: true,
                message: "Please enter an sensor name",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t("Device ID")}
            name="dev_eui"
            rules={[
              {
                required: true,
                message: "Please enter an device id",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("Min Temperature")}
            name="min_temp"
            rules={[
              {
                required: true,
                message: "Please enter an min temp",
                type: "number",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={-30} max={120} />
          </Form.Item>

          <Form.Item
            label={t("Max Temperature")}
            name="max_temp"
            rules={[
              {
                required: true,
                message: "Please enter an max temp",
                type: "number",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={-30} max={120} />
          </Form.Item>

          <Form.Item
            label={t("Temperature Unit")}
            name="temperature_unit"
            rules={[
              {
                required: true,
                message: "Please enter an temperature unit",
              },
            ]}
          >
            <Select options={[{ label: "C", value: "C" }]} />
          </Form.Item>

          <Form.Item label={t("Alert")} name="alert">
            <Switch />
          </Form.Item>

          <Form.Item
            label={t("Delay for alert")}
            name="delay_before_alert"
            rules={[
              {
                required: true,
                message: "Please enter an delay alert",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t("Alert Unit")}
            name="delay_unit"
            rules={[
              {
                required: true,
                message: "Please enter an alert unit",
              },
            ]}
          >
            <Select options={[{ label: "minutes", value: "minutes" }]} />
          </Form.Item>

          <Flex justify="flex-end">
            <Button
              type="default"
              style={{ marginRight: 8 }}
              onClick={() => setIsDrawer(false)}
            >
              {t("Discard")}
            </Button>
            <Button type="primary" htmlType="submit">
              {t("Save")} {<SendOutlined />}
            </Button>
          </Flex>
        </Form>
      </Drawer>
    </Card>
  );
};

export default Restaruant;
